import { useEffect, useState } from "react";
import { z } from "zod";
import {
  type ForwardComposition,
  ForwardCompositionSchema,
} from "../../src/inbox-v2/forward-contract.ts";
import { unsentKey } from "./unsent-reply.ts";

const DraftSchema = z.strictObject({
  composition: ForwardCompositionSchema,
  to: z.string(),
  cc: z.string(),
  bcc: z.string(),
  note: z.string(),
  html: z.string(),
  text: z.string(),
  format: z.enum(["html", "text"]),
  omitImages: z.boolean(),
  unsupportedImages: z.boolean().default(false),
  /**
   * Written by builds that copied the original's files into the forward, before Bison was seen to
   * attach them itself (2026-10-23). Read so such a draft still opens; nothing uses it now.
   */
  originals: z.enum(["pending", "included", "omitted"]).optional(),
  at: z.number(),
});
type Draft = z.infer<typeof DraftSchema>;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function read(key: string): Draft | null {
  try {
    const value = DraftSchema.safeParse(JSON.parse(localStorage.getItem(key) ?? "null"));
    return value.success && Date.now() - value.data.at < WEEK_MS ? value.data : null;
  } catch {
    return null;
  }
}

export function forwardDraftKey(
  userId: string,
  workspaceId: string,
  planId: string,
  messageId: string,
) {
  return `${unsentKey(userId, workspaceId, planId)}:forward:${encodeURIComponent(messageId)}`;
}

/** Why a saved forward must be refreshed before it can be sent, or null when it need not be. */
export type StaleForward = "source" | "composition" | null;

/**
 * The body and recipients belong to this source and person, even after a reload or newer inbound.
 * A saved draft composed from an earlier reading of its source (the files' status changed, say) is
 * `stale` until the rep refreshes it, because the server refuses to queue it
 * (`docs/2026-10-22-forward-release-decisions.md`, Change B).
 *
 * A draft of the same source that LeadCue composed differently (an older build's forwarded header,
 * QF4 2026-10-23) never sends that older composition: untouched, it takes the current one at once,
 * keeping the rep's words and recipients; with the forwarded email edited, it asks for Refresh, as
 * a changed source does. Until then it is not `current`, and cannot be sent.
 */
export function useForwardDraft(key: string, composition: ForwardComposition) {
  // Whether this forward starts afresh, with nothing kept: only then does it take the signature.
  const [fresh] = useState(() => read(key) === null);
  const [draft, setDraft] = useState<Draft>(
    () =>
      read(key) ?? {
        composition,
        to: "",
        cc: "",
        bcc: "",
        note: "",
        html: composition.html,
        text: composition.plainText,
        format: "html",
        omitImages: false,
        unsupportedImages: false,
        at: Date.now(),
      },
  );
  const [saved, setSaved] = useState(true);
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify({ ...draft, at: Date.now() }));
      setSaved(true);
    } catch {
      setSaved(false);
    }
  }, [key, draft]);
  // Taken as soon as it is seen, whether the draft was saved by an older build or the composition
  // was read again since; until then the draft is not `current`.
  const quietly = recomposedOnly(draft, composition) && !quoteEdited(draft);
  useEffect(() => {
    if (quietly) setDraft((current) => recomposed(current, composition));
  }, [quietly, composition]);
  const stale: StaleForward =
    composedFrom(draft.composition) !== composedFrom(composition)
      ? "source"
      : recomposedOnly(draft, composition)
        ? "composition"
        : null;
  return {
    draft,
    fresh,
    saved,
    /** Why Refresh is needed first; a draft taking the current composition by itself needs none. */
    stale: quietly ? null : stale,
    /** The draft holds exactly the composition just read: the only one that may be sent. */
    current: stale === null,
    change: (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch })),
    /**
     * Compose again from the source as it is now. The note and recipients stay; edits to the quoted
     * message do not, and the images are reviewed again.
     */
    refresh: () =>
      setDraft((current) => ({
        ...current,
        composition,
        html: composition.html,
        text: composition.plainText,
        omitImages: false,
        unsupportedImages: false,
      })),
  };
}

/** A key for the forwarded email as composed, so an editor holding a copy of it starts afresh. */
export function compositionKey(composition: ForwardComposition): string {
  // FNV-1a over the composed body: a recomposition of the same source keeps both fingerprints.
  let hash = 0x811c9dc5;
  for (const character of `${composition.html}\u0000${composition.plainText}`) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${composedFrom(composition)}:${hash.toString(16)}`;
}

/** What the server checks a queued forward against: the source as read, and as held here. */
function composedFrom(composition: ForwardComposition): string {
  return `${composition.source.fingerprint}:${composition.localFingerprint}`;
}

/** The same source, which LeadCue now composes differently. */
function recomposedOnly(draft: Draft, composition: ForwardComposition): boolean {
  return (
    composedFrom(draft.composition) === composedFrom(composition) &&
    (draft.composition.html !== composition.html ||
      draft.composition.plainText !== composition.plainText ||
      draft.composition.omittedImages !== composition.omittedImages)
  );
}

/** The rep changed the forwarded email itself, not only the words above it. */
function quoteEdited(draft: Draft): boolean {
  return draft.html !== draft.composition.html || draft.text !== draft.composition.plainText;
}

/** The draft on the current composition, the rep's words, recipients and choices kept. */
function recomposed(draft: Draft, composition: ForwardComposition): Draft {
  return {
    ...draft,
    composition,
    html: composition.html,
    text: composition.plainText,
    omitImages:
      composition.omittedImages === draft.composition.omittedImages ? draft.omitImages : false,
    unsupportedImages: false,
  };
}
